<template>
	<el-dialog :title="payDialogData.title" :visible.sync="dialogVisible" :close-on-click-modal="false"
		:close-on-press-escape="false" append-to-body @close="handleClose" @closed="handleClosed">
		<div class="pay-body">
			<!-- 金额 -->
			<div class="amount">
				<span class="amount-label">应付金额</span>
				<span class="amount-value">¥{{ payDialogData.amount }}</span>
			</div>

			<!-- 支付方式 -->
			<div class="pay-list">
				<div v-for="item in payDialogData.payList" :key="item.id" class="pay-item"
					:class="{ active: currentPayId === item.id }" @click="selectPay(item)">
					<img :src="item.logo" class="pay-logo" alt="" />
					<span class="pay-name">{{ item.name }}</span>
					<i v-if="currentPayId === item.id" class="el-icon-check check-icon"></i>
				</div>
			</div>

			<!-- 二维码:payData 不为空才展示 -->
			<div class="qr-section">
				<template v-if="hasQr">
					<img :src="payDialogData.payData.protocal" class="qr" alt="支付二维码" />
					<p class="tip">{{ statusText }}</p>
				</template>
				<div v-else class="qr-empty">
					<p>{{ emptyTip }}</p>
				</div>
			</div>
		</div>

		<span slot="footer" class="dialog-footer">
			<el-button class="dialog-close" @click="dialogVisible = false">关 闭</el-button>
		</span>
	</el-dialog>
</template>

<script>
	import {
		queryPayResult,
		queryPayState
	} from "@/api/pay"
	
	import {
		PayState
	} from "@/utils/const"

	export default {
		name: 'PayDialog',

		props: {
			// 弹窗显隐(配合 .sync)
			visible: {
				type: Boolean,
				default: false
			},
			// 整个支付弹窗数据对象
			payDialogData: {
				type: Object,
				default: () => ({
					title: '',
					amount: '',
					payList: [],
					payData: {
						out_trade_no: '',
						qr_url: ''
					},
				}),
			},
			// 轮询间隔
			interval: {
				type: Number,
				default: 2000
			},
			// 最长轮询时间
			timeout: {
				type: Number,
				default: 5 * 60 * 1000
			},
		},

		data() {
			return {
				dialogVisible: false,
				currentPayId: '', // 当前选中的支付方式 id
				paying: false, // 是否在轮询
				expired: false, // 二维码是否过期
				tipText: '', // 自定义提示
				errorCount: 0,
				startAt: 0,
				timer: null,
				stopped: true,
			}
		},

		computed: {
			// 是否有二维码数据
			hasQr() {
				const d = this.payDialogData.payData || {}
				return !!(d.protocal && d.out_trade_no)
			},

			// 空二维码时的提示
			emptyTip() {
				if (!this.payDialogData.payList || !this.payDialogData.payList.length) {
					return '暂无可用支付方式'
				}
				if (!this.currentPayId) return '请选择支付方式'
				return '二维码生成中…'
			},

			statusText() {
				if (this.tipText) return this.tipText
				if (this.expired) return '二维码已过期,请重新选择支付方式'
				if (this.paying) return '等待支付中…'
				return '请使用手机扫码支付'
			},

			// 当前订单号
			outTradeNo() {
				return (this.payDialogData.payData || {}).out_trade_no || ''
			},
		},

		watch: {
			// 显隐同步 + 打开时初始化
			visible: {
				immediate: true,
				handler(v) {
					this.dialogVisible = v
					if (v) {
						this.initDialog()
					} else {
						this.stop()
					}
				},
			},

			// 内部关闭 → 同步回父组件 + 停轮询
			dialogVisible(v) {
				if (v !== this.visible) {
					this.$emit('update:visible', v)
				}
				if (!v) this.stop()
			},

			// payData 变化(父组件异步塞进二维码)→ 有订单号就开轮询
			'payDialogData.payData.out_trade_no'(val) {
				if (val && this.dialogVisible) {
					this.startAt = Date.now()
					this.expired = false
					this.tipText = ''
					this.start()
				} else {
					this.stop()
				}
			},
		},

		mounted() {
			document.addEventListener('visibilitychange', this.handleVisibility)
		},

		beforeDestroy() {
			document.removeEventListener('visibilitychange', this.handleVisibility)
			this.stop()
		},

		methods: {
			// 打开时初始化:默认选中第一个支付方式
			initDialog() {
				this.expired = false
				this.tipText = ''
				this.errorCount = 0
				this.currentPayId = this.payDialogData.defaultId;

				const list = this.payDialogData.payList || []
				if (list.length) {
					// 默认选中第一个(如果当前选中项已不在列表里,也重置为第一个)
					const exist = list.some((i) => i.id === this.currentPayId)
					if (!exist) this.currentPayId = list[0].id
				} else {
					this.currentPayId = ''
				}

				// 如果传进来时 payData 已有二维码,直接开轮询
				if (this.hasQr) {
					this.startAt = Date.now()
					this.start()
				}
			},

			// 切换支付方式
			selectPay(item) {
				if (this.currentPayId === item.id) return
				// this.currentPayId = item.id
				
				if(item['clickable'] != 1){
					return;
				}
				
				this.stop()
				this.expired = false
				this.tipText = ''
				// 通知父组件:用户切换了支付方式,请重新下单拿对应二维码
				this.$emit('change-pay', item)
				// 若父组件立即回填 payData,watch 会自动 start()
			},

			start() {
				if (!this.outTradeNo) return
				if (!this.stopped) return
				this.stopped = false
				this.paying = true
				this.loop()
			},

			stop() {
				this.stopped = true
				this.paying = false
				if (this.timer) {
					clearTimeout(this.timer)
					this.timer = null
				}
			},

			async loop() {
				if (this.stopped) return

				if (Date.now() - this.startAt > this.timeout) {
					this.stop()
					this.tipText = '支付超时,请重新选择支付方式'
					return
				}

				try {
					const res = await queryPayState({
						'out_trade_no': this.outTradeNo
					})
					if (this.stopped) return

					let realData = res.data;
					if (realData.state === PayState.Suc) {
						this.stop()
						
						let pay = this.payDialogData.payData;
						console.log('pay suc', pay)
						this.$emit('success', pay)
						this.dialogVisible = false
						return
					}
					this.errorCount = 0
				} catch (e) {
					this.errorCount++
					if (this.errorCount >= 5) {
						this.stop()
						this.tipText = '网络异常,请稍后重试'
						return
					}
				}

				if (this.stopped) return
				this.timer = setTimeout(this.loop, this.interval)
			},

			handleVisibility() {
				if (!this.dialogVisible) return
				if (document.visibilityState === 'hidden') {
					this.stop()
				} else if (this.outTradeNo) {
					this.start() 
				}
			},

			handleClose() {
				this.stop()
				this.$emit('close')
			},

			handleClosed() {
				this.expired = false
				this.tipText = ''
				this.errorCount = 0
				this.currentPayId = ''
			},
		},
	}
</script>

<style rel="stylesheet/scss" lang="scss" scoped>
	@use "index.scss";
</style>